import { useRef } from "react";
import { Camera, X, Image as ImageIcon } from "lucide-react";

// Compress image client-side before base64
async function compressImage(file, maxDim = 1600, quality = 0.82) {
  const blobUrl = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = blobUrl;
    });
    const ratio = Math.min(1, maxDim / Math.max(img.width, img.height));
    const w = Math.round(img.width * ratio);
    const h = Math.round(img.height * ratio);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    canvas.getContext("2d").drawImage(img, 0, 0, w, h);
    return canvas.toDataURL("image/jpeg", quality);
  } finally {
    URL.revokeObjectURL(blobUrl);
  }
}

export default function PhotoCapture({ photos, onChange, testid = "photo-capture" }) {
  const cameraInput = useRef(null);
  const galleryInput = useRef(null);

  const handleFiles = async (files) => {
    if (!files || !files.length) return;
    const newOnes = [];
    for (const file of files) {
      try {
        const dataUrl = await compressImage(file);
        newOnes.push(dataUrl);
      } catch (e) {
        console.error("compress failed", e);
      }
    }
    onChange?.([...(photos || []), ...newOnes]);
  };

  const removeAt = (idx) => {
    const next = photos.filter((_, i) => i !== idx);
    onChange?.(next);
  };

  return (
    <div data-testid={testid} className="space-y-3">
      <div className="flex gap-2">
        <button
          data-testid="photo-capture-btn"
          type="button"
          onClick={() => cameraInput.current?.click()}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[var(--vidoc-primary)] text-white font-medium touch-target hover:bg-[var(--vidoc-primary-dark)]"
        >
          <Camera className="w-5 h-5" />
          Prendre une photo
        </button>
        <button
          data-testid="photo-gallery-btn"
          type="button"
          onClick={() => galleryInput.current?.click()}
          className="px-4 py-3 rounded-xl bg-[var(--vidoc-bg-secondary)] text-[var(--vidoc-text)] font-medium touch-target hover:bg-[var(--vidoc-bg-tertiary)]"
        >
          <ImageIcon className="w-5 h-5" />
        </button>
      </div>
      <input
        ref={cameraInput}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
      <input
        ref={galleryInput}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />

      {photos?.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {photos.map((src, idx) => (
            <div
              key={src}
              data-testid={`photo-thumb-${idx}`}
              className="relative aspect-square rounded-lg overflow-hidden bg-[var(--vidoc-bg-secondary)]"
            >
              <img src={src} alt={`Photo ${idx + 1}`} className="w-full h-full object-cover" />
              <button
                data-testid={`photo-remove-${idx}`}
                type="button"
                onClick={() => removeAt(idx)}
                className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-black"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
